import React from 'react'
import Yup from '../../../helpers/custom-yup.js'
import TicketSaleSelectors from '../../../ducks/ticket-sale/ticket-sale.selectors'
import BusSeatSelectionSelectors from '../../../selectors/bus-seat-selection.selectors'
import { connect } from 'react-redux'
import { Formik, Form } from 'formik'
import PaymentFields, {
	paymentFieldsYupSchema,
	paymentMuvicardFieldsYupSchema,
	paymentMuvicardOneClickYupSchema,
	paymentFieldsInitialValues
} from '../../../components/form-components/payment-fields/payment-fields.component'
import TicketSaleService from './../../../services/ticket-sale.service'
import { PaymentService } from './../../../services/payment.service'
import { PAYMENT_METHODS } from '../../../constants'

const PAYMENT_METHODS_RETRY_INTERVAL_MS = 5000

class PaymentDataComponent extends React.Component {
	constructor(props) {
		super(props)

		this.state = {
			bankSlipParameters: {},
			apiPaymentMethods: null,
			apiInstallments: null,
			apiPaymentMethodsLoading: true
		}

		this.paymentMethodsRetryTimer = null
	}

	shouldComponentUpdate(nextProps, nextState) {
		if (
			this.props.totalValue == nextProps.totalValue &&
			this.props.paymentMethod == nextProps.paymentMethod &&
			this.props.isCalculatingValues == nextProps.isCalculatingValues &&
			this.state.bankSlipParameters == nextState.bankSlipParameters &&
			this.state.apiPaymentMethods == nextState.apiPaymentMethods &&
			this.state.apiInstallments == nextState.apiInstallments &&
			this.state.apiPaymentMethodsLoading == nextState.apiPaymentMethodsLoading
		) {
			return false
		}

		return true
	}

	componentWillUnmount() {
		this._isMounted = false
		clearTimeout(this.paymentMethodsRetryTimer)
	}

	async componentDidMount() {
		this._isMounted = true
		this.props.getFormRef(this.refs.form)

		try {
			const { success, data } = await TicketSaleService.getBankSlipParameters()

			if (success) {
				this.setState({ bankSlipParameters: data })
			}
		} catch (error) {
			// keep the initial bankSlipParameters value
		}

		// Com um recalculo em curso (cupom automatico na montagem da tela), o total ainda vai
		// mudar: buscar agora gastaria a chamada a toa e exibiria o parcelamento do valor de
		// tabela. O componentDidUpdate busca assim que o recalculo terminar.
		//
		// A leitura da flag so e confiavel aqui porque ela vem DEPOIS do await acima: este
		// componente e montado antes do DiscountCoupon (quem liga a flag), entao no inicio do
		// componentDidMount ela ainda seria false. Se essa ordem mudar, o pior caso e voltar a
		// buscar duas vezes - como era antes - e nunca deixar a tela sem tabela de parcelas.
		if (!this.props.isCalculatingValues) {
			await this.loadPaymentMethods()
		}
	}

	async componentDidUpdate(prevProps) {
		// Fees/parcelas do cartão e do muvicard dependem do total do carrinho -
		// qualquer coisa que altere o totals (cupom, remoção de poltrona, etc.)
		// precisa recalcular payments/methods, senão a tela mostra parcelamento
		// calculado em cima do total antigo.
		if (this.props.isCalculatingValues) return

		const totalChanged = prevProps.totalValue !== this.props.totalValue
		// Fim do recalculo: cobre tanto o caso em que a montagem foi pulada quanto o cupom que
		// termina sem alterar o total (nenhum aplicavel / falha) - sem isto a tela ficaria sem
		// tabela de parcelas.
		const finishedCalculating =
			prevProps.isCalculatingValues && !this.props.isCalculatingValues

		if (totalChanged || finishedCalculating) {
			await this.loadPaymentMethods()
		}
	}

	/**
	 * Fetches payment methods, fees and installment options for the current
	 * cart (payments/methods) - the single source PaymentFields/credit-card
	 * /muvicard-fields read from, kept in sync with the cart's totals.
	 */
	async loadPaymentMethods() {
		clearTimeout(this.paymentMethodsRetryTimer)
		this.setState({ apiPaymentMethodsLoading: true })

		try {
			// D10: com o Muvicard 1-clique ativo a grade vem sem o desconto de 1a compra (o fechamento nao o aplica).
			const { user } = this.props
			const { methods, installments, installmentsByMethod } =
				await PaymentService.getMethods({
					oneClickBuy: !!(user && user.muvicardData && user.muvicardData.oneClickBuy)
				})

			// Bug 7877: a tabela de parcelas passa a ser POR METODO ({ CreditCard, Muvicard }).
			// Prioriza o campo aninhado; se o Hub ainda nao o expoe (deploy antigo), reaproveita
			// o campo plano `installments` como o grid do CARTAO. Sem a chave de um metodo, a folha
			// correspondente cai no fallback legado por metodo - entao o Muvicard fica correto mesmo
			// contra um Hub antigo.
			const byMethod =
				installmentsByMethod ||
				(Array.isArray(installments) && installments.length
					? { CreditCard: installments }
					: null)

			this.setState({
				apiPaymentMethods: methods,
				apiInstallments: byMethod
			})
		} catch (error) {
			// No checkout novo payments/methods e a fonte unica (sem fallback pro endpoint
			// legado - ver disableLegacyInstallments): continua tentando enquanto o erro
			// persistir e o usuario seguir na tela de checkout.
			this.scheduleLoadPaymentMethodsRetry()
		} finally {
			this.setState({ apiPaymentMethodsLoading: false })
		}
	}

	scheduleLoadPaymentMethodsRetry() {
		this.paymentMethodsRetryTimer = setTimeout(() => {
			// Para de tentar se a tela foi desmontada ou se a compra ja foi concluida
			// (o checkout permanece montado, so escondido, ate o fim do fluxo).
			if (!this._isMounted || this.props.isConfirmationStep) return

			this.loadPaymentMethods()
		}, PAYMENT_METHODS_RETRY_INTERVAL_MS)
	}

	renderFormik = props => {
		const { busCompany } = this.props

		const {
			bankSlipParameters,
			apiPaymentMethods,
			apiInstallments,
			apiPaymentMethodsLoading
		} = this.state

		const isNotPartnerCompany = !(
			busCompany == 'Planalto' ||
			busCompany == 'Muvibus' ||
			busCompany == 'Planalto2'
		)

		return (
			<Form noValidate>
				<PaymentFields
					{...props}
					noOnlineDebt
					isNotPartnerCompany={isNotPartnerCompany}
					noBankSlip={false}
					orderValue={this.props.totalValue}
					onCompleteCpf={cpf => {
						this.props.handleCompleteCpf(cpf)
					}}
					bankSlipParameters={bankSlipParameters}
					apiPaymentMethods={apiPaymentMethods}
					apiInstallments={apiInstallments}
					apiPaymentMethodsLoading={apiPaymentMethodsLoading}
					disableLegacyInstallments={this.props.disableLegacyInstallments}
				/>
			</Form>
		)
	}

	render() {
		const schema = Yup.object().shape(paymentFieldsYupSchema)
		const muvischema = Yup.object().shape(paymentMuvicardFieldsYupSchema)
		const oneClickMuviSchema = Yup.object().shape(
			paymentMuvicardOneClickYupSchema
		)
		const initialValues = paymentFieldsInitialValues({ installments: true })
		const { paymentMethod, user } = this.props

		let schemaCard = ''

		if (paymentMethod == PAYMENT_METHODS.MUVICARD) {
			if (user && user.muvicardData && user.muvicardData.oneClickBuy) {
				schemaCard = oneClickMuviSchema
			} else {
				schemaCard = muvischema
			}
		} else {
			schemaCard = schema
		}

		return (
			<React.Fragment>
				<Formik
					ref="form"
					validationSchema={schemaCard}
					render={this.renderFormik}
					initialValues={initialValues}
					onSubmit={this.props.onSubmit}
				/>
			</React.Fragment>
		)
	}
}

const mapStateToProps = store => ({
	user: store.user,
	discountLabel: store.ticketSale.discountLabel,
	discountValue: store.ticketSale.discountValue,
	discountPercent: store.ticketSale.discountPercent,
	tripPriceGoing: TicketSaleSelectors.getTripPriceGoing(store),
	totalPointsOnTicketSale: store.ticketSale.totalPointsOnTicketSale,
	discountPercentage: store.ticketSale.discountCoupon.discountPercentage,
	tripPriceReturn: TicketSaleSelectors.getTripPriceReturn(store),
	serviceCharge: TicketSaleSelectors.getServiceChargeValue(store),
	selectedBusSeatsGoing: BusSeatSelectionSelectors.getSelectedBusSeatsGoing(
		store.busSeatSelection
	),
	selectedBusSeatsReturn: BusSeatSelectionSelectors.getSelectedBusSeatsReturn(
		store.busSeatSelection
	),
	paymentMethod: store.ticketSale.paymentMethod,
	isCalculatingValues: store.ticketSale.isCalculatingValues,
	isConfirmationStep: TicketSaleSelectors.isConfirmationStep(store),
	selectedPoltonasIda: BusSeatSelectionSelectors.getSelectedViagemIda(
		store.busSeatSelection
	),
	calculatedValues: TicketSaleSelectors.getCalculatedValues(store)
})

const PaymentData = connect(mapStateToProps, null)(PaymentDataComponent)

export default PaymentData
